import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';
import { StateService } from '@uirouter/angularjs';

import {
    ActualDataType,
    AggregationMode,
    ComponentDefinition,
    Dashboard,
    DateOrder,
    Dictionary,
    DigitalSetSimpleDictionary,
    DigitalState,
    Group,
    IValueViewModel,
    Limit,
    RowDefinition,
    TagDateValue,
    TagStringValueDto,
    WidgetValues
} from '../../models';
import { PivotTableWidget } from './pivotTable.widget';
import { getHeadings } from './getHeadings';
import { BuiltRow } from './BuiltRow';
import { IAggregationHelper, IDashboardHelperService, IFormatNumberHelper, IStringValuesHelperService } from '../../services/helpers';
import { guidEmpty } from '../../common/constants';
import { IDateRangeService, ITranslatedLoggerService } from '@services/general';
import { toDictionary } from '../../common/helpers';
import { PivotTableRowSeriesDefinition } from './PivotTableRowSeriesDefinition';
import { PivotTableSeriesDefinition } from './PivotTableSeriesDefinition';
import { IExportToCsvService } from '@services/data';
import { ISeriesSortingConfiguration } from './ISeriesSortingConfiguration';
import { formatValueForCsv } from '../../services/data/formatValueForCsv';

const missingLabel = '(missing)';

class PivotTableController implements angular.IComponentController {

    // Bound variables coming from outside
    readonly boundData: WidgetValues;
    readonly dashboards: Dashboard[];
    readonly digitalSetDictionary: DigitalSetSimpleDictionary;
    readonly isEditMode: boolean;
    readonly item: PivotTableWidget;
    readonly selectedGroup: Group;
    readonly stringValues: TagStringValueDto[];
    readonly viewModels: Dictionary<IValueViewModel>;

    // Variables filled in this class, and not exposed
    body: (RowDefinition & { value?: number; })[][];
    headings: BuiltRow[];

    static $inject = [
        '$state',
        'aggregationHelper',
        'dashboardHelperService',
        'dateRangeService',
        'exportToCsvService',
        'formatNumberHelper',
        'translatedLoggerService',
        'stringValuesHelperService'
    ];

    constructor(
        private _$state: StateService,
        private _aggregationHelper: IAggregationHelper,
        private _dashboardHelperService: IDashboardHelperService,
        private _dateRangeService: IDateRangeService,
        private _exportToCsvService: IExportToCsvService,
        private _formatNumberHelper: IFormatNumberHelper,
        private _loggerService: ITranslatedLoggerService,
        private _stringValuesHelperService: IStringValuesHelperService
    ) {
    }

    $onInit() {
        this.headings = this.headings || [];
        this.body = this.body || [];

        (this.item as any).exportToCsv = () => {
            try {
                const formatter = this._formatNumberHelper.getFormatter(null, false);
                const dataArray: string[][] = this.headings.map(d => d.cells).map(c => c.map(d => d.value))
                    .concat(this.body.map(b => b.map(c => c.value || c.value === 0 ? formatValueForCsv(c.value, formatter) : c.content)));
                this._exportToCsvService.getCsvFromData(dataArray,
                    `PivotTable_${this.item.name}_${moment(this._dateRangeService.beginDate())
                        .format('YYYYMMDD_HHmm')}.csv`);
            } catch (e) {
                this._loggerService.logError('Failed to export pivot table to csv.');
                this._loggerService.logError(e.message);
            }
        };
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (this.viewModels && this.item) {
            // Might not be the most efficient to do it every time...
            let series = this.item.configuration.series
                .sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1);
            let rowSeries = this.item.configuration.rowSeries;
            if (this.item.map) {
                series = series.map(s => {
                    const mapped = this.item.map[s.guid];
                    if (mapped) {
                        return Object.assign({}, s, { guid: mapped });
                    }
                    return s;
                });
                if (this.item.map[rowSeries.guid]) {
                    rowSeries = Object.assign({}, rowSeries, { guid: this.item.map[rowSeries.guid] });
                }
            }
            const allSeries: { guid: string; value: string; }[] =
                [{ guid: rowSeries.guid, value: rowSeries.value + 'row' }]
                    .concat(series);
            const vm = this.viewModels[rowSeries.guid];
            if (vm) {
                this.headings = getHeadings(this.item.configuration, allSeries, this.viewModels);
            }

            if (changesObj['boundData'] && this.boundData) {
                const { values } = this.boundData;
                if (values && values.length) {
                    if (vm) {
                        const rowValues = this.getRowValues(values, vm, rowSeries, this.item.configuration);
                        const body = this.buildBody(rowValues, series, values, rowSeries);
                        if (this.item.configuration.dashboardGuid && this.item.configuration.dashboardGuid !== guidEmpty) {
                            const links: RowDefinition[] = allSeries.map(s => {
                                const vm = this.viewModels[s.guid];
                                return {
                                    content: null,
                                    link: {
                                        classed: {
                                            'fa-th-large': true
                                        },
                                        stateName: 'dashboard',
                                        params: {
                                            groupGuid: vm && vm.groupList && vm.groupList.length ? vm.groupList[0].guid : null
                                        }
                                    },
                                    classed: {
                                        'centered': true
                                    }
                                };
                            });
                            if (this.item.configuration.aggregationColumnMode !== AggregationMode.None) {
                                links.push({ content: '' });
                            }
                            body.push(links);
                        }
                        this.body = body;
                    }
                }
            }
        }
    }

    redirect(cell: RowDefinition): void {
        const { dashboardGuid } = this.item.configuration;
        if (dashboardGuid && dashboardGuid !== guidEmpty) {
            const selectedGuid = this._dashboardHelperService.getSelectedGroupGuid(this.selectedGroup?.guid, cell);
            const beginDate = this._dateRangeService.beginDate() as string;
            const endDate = this._dateRangeService.endDate() as string;
            const go = this._dashboardHelperService.getGotoDestination(dashboardGuid, toDictionary(this.dashboards), selectedGuid, beginDate, endDate);
            this._$state.go(go.route, go.params);
        }
    }

    private getRowValues(
        values: TagDateValue[],
        vm: IValueViewModel,
        rowSeries: PivotTableRowSeriesDefinition,
        config: ISeriesSortingConfiguration
    ): TagDateValue[] {
        let rowValues: TagDateValue[] = values
            .filter(v => v.guid === rowSeries.guid
                && v.valueType === rowSeries.value
                && !!v.comment
                && v.secondaryValue === 0); //Set in backend as a marker for row series
        if (vm.actualDataType === ActualDataType.Digital) {
            rowValues = this.mapDigitalValues(vm, rowValues, rowSeries);
        }
        if (vm.actualDataType === ActualDataType.String) {
            rowValues = this.mapStringValues(vm, rowValues);
        }
        if (config) {
            const { sortBy, limit, maxItems, sortOrder } = config;
            if (!sortBy) {
                rowValues = this.sortValues(rowValues, sortOrder);
            } else {
                let matchingValues = values.filter(v =>
                    v.guid + v.valueType === sortBy
                    && v.secondaryValue !== 0
                    && v.value !== null
                );
                const matchingDates = matchingValues.map(d => d.timeStamp);
                const missing = rowValues.filter(rv => matchingDates.indexOf(rv.timeStamp) < 0)
                    .map(rv => {
                        const tdv: TagDateValue = {
                            value: null,
                            timeStamp: rv.timeStamp,
                            comment: rv.comment,
                            guid: rv.guid,
                            valueType: rv.valueType,
                            secondaryValue: rv.value
                        };
                        return tdv;
                    });
                matchingValues = matchingValues
                    .concat(missing);
                matchingValues = this.sortValues(matchingValues, sortOrder);
                rowValues = matchingValues
                    .map(mv => {
                        const rv = rowValues.filter(d => {
                            let isMatching = d.timeStamp === mv.timeStamp;
                            if (isMatching && !mv.value) {
                                isMatching = d.value === mv.secondaryValue; // Set above, to avoid duplicate comments
                            }
                            return isMatching;
                        });
                        if (rv.length) {
                            return rv[0];
                        }
                        return null;
                    })
                    .filter(d => !!d);
            }
            if (maxItems > 0) {
                switch (limit) {
                    case Limit.None:
                        break;
                    case Limit.Top:
                        rowValues = rowValues.splice(0, maxItems);
                        break;
                    case Limit.Bottom:
                        rowValues = rowValues.reverse()
                            .splice(0, maxItems)
                            .reverse();
                        break;
                }
            }
        } else {
            rowValues = rowValues.sort((a, b) => a.value > b.value ? 1 : -1);
        }
        return rowValues;
    }

    private sortValues(values: TagDateValue[], sortOrder: DateOrder): TagDateValue[] {
        switch (sortOrder) {
            case DateOrder.Increasing:
                values = values.sort((a, b) => a.value > b.value ? 1 : -1);
                break;
            case DateOrder.Decreasing:
                values = values.sort((a, b) => a.value < b.value ? 1 : -1);
                break;
        }
        return values;
    }

    private mapDigitalValues(vm: IValueViewModel, rowValues: TagDateValue[], rowSeries: PivotTableRowSeriesDefinition) {
        const digitalSet = this.digitalSetDictionary[vm.digitalSetGuid];
        if (!!digitalSet) {
            const stateCollection: DigitalState[] = [];
            for (const key of Object.keys(digitalSet)) {
                stateCollection.push({ code: parseInt(key), name: digitalSet[key] });
            }
            stateCollection.push({ code: Number.MAX_SAFE_INTEGER, name: missingLabel });
            rowValues = stateCollection.map(d => d)
                // .sort((a, b) => a.code > b.code ? 1 : -1)
                .map(d => {
                    let found = rowValues.filter(rv => rv.value === d.code);
                    if (!found.length) {
                        found = [{
                            value: d.code,
                            valueType: rowSeries.value,
                            guid: rowSeries.guid,
                            timeStamp: new Date().toISOString(),
                            comment: d.name
                        }];
                    }
                    return found[0];
                });
        }
        return rowValues;
    }

    private mapStringValues(vm: IValueViewModel, rowValues: TagDateValue[]) {
        if (this.stringValues?.length > 0) {
            rowValues = this._stringValuesHelperService.mapStringForTagDatesValuesForOne(rowValues, this.stringValues, vm, this.item.configuration.aggregationColumnMode);
        }
        return rowValues;
    }

    private buildBody(rowValues: TagDateValue[], series: PivotTableSeriesDefinition[], values: TagDateValue[], rowSeries: PivotTableRowSeriesDefinition) {
        return rowValues.map(rv => {
            const cells: (RowDefinition & { value?: number; })[] = series.map(s => {
                const c = _.find(values, v => v.guid === s.guid
                    && v.valueType === s.value
                    && v.timeStamp === rv.timeStamp // timestamp is used as the key to identify rows matching with aggregation
                    && v.secondaryValue !== 0 // Differenciator set in backend
                );
                const vm = this.viewModels[s.guid];
                const formattedValue = vm && vm.actualDataType === ActualDataType.String
                    ? this._stringValuesHelperService.getValueOrString(vm, c, this.stringValues, this.item.configuration.aggregationColumnMode)
                    : (!!c && c.value !== null) ? d3.format(`.${s.decimals}f`)(c.value) : '-';
                
                const color = !!c ? ((s.hasAlarms && c.alarm > 0) ? null : s.color) : null;
                const alarm = !!c ? (s.hasAlarms ? c.alarm : null) : null;
                return {
                    content: formattedValue,
                    classed: {
                        'number-cell': true,
                        'alert-danger': alarm === 3,
                        'alert-warning': alarm === 2,
                        'alert-info': alarm === 1
                    },
                    style: { 'color': color },
                    value: !!c ? c.value : null
                };
            });
            cells.unshift({ content: rv.comment });
            if (this.item.configuration.aggregationColumnMode !== AggregationMode.None) {
                const values = cells.filter(c => c.value !== null && c.value !== undefined).map(c => { return { value: c.value }; });
                const aggregated = this._aggregationHelper.aggregate(values, this.item.configuration.aggregationColumnMode);
                cells.push({
                    content: d3.format(`.${rowSeries.decimals ?? 2}f`)(aggregated),
                    classed: { 'number-cell': true }
                });
            }
            return cells;
        });
    }
}

export const wtPivotTable: ComponentDefinition = {
    componentId: 'wtPivotTable',
    controller: PivotTableController,
    template: require('./pivotTable.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<',
        dashboards: '<',
        digitalSetDictionary: '<?',
        isEditMode: '<?',
        item: '<',
        selectedGroup: '<',
        stringValues: '<',
        viewModels: '<'
    }
};